import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  adminGetUser,
  adminDeleteUser,
  adminSendOobCode,
  adminSetAppAccess,
  adminDismissLicenseRequest
} from "../lib/callables";
import { Spinner } from "../components/spinner";
import { ProviderBadge } from "../components/provider-badge";
import { useAuthCtx } from "../lib/auth-provider";
import { relativeTime } from "../lib/relative-time";
import type { AdminUserDetail, AppDefinition, AppGrant } from "@acruxsoft/platform-shared";
import { APPS, isGrantCurrent } from "@acruxsoft/platform-shared";

function fmtDateShort(ms: number, locale: string): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}

export function AdminUserDetailPage() {
  const { uid = "" } = useParams<{ uid: string }>();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminGetUser({ uid });
      setData(data);
    } catch (e) {
      console.error("[admin-user-detail] load failed", e);
      setError(t("adminPanel.detail.loadError"));
    } finally {
      setLoading(false);
    }
  }, [uid, t]);

  useEffect(() => {
    if (uid) reload();
  }, [uid, reload]);

  // The request inbox and the notification email link to #apps. The router
  // doesn't scroll to hashes, and the card only exists once the data is in.
  const { hash } = useLocation();
  useEffect(() => {
    if (data && hash === "#apps") document.getElementById("apps")?.scrollIntoView({ block: "start" });
  }, [data, hash]);

  if (loading) return <Spinner />;
  if (error) return <div className="acx__alert acx__alert--danger" role="alert">{error}</div>;
  if (!data) return null;

  const locale = i18n.language;

  // Frank is not technical: the page is the user in one line, the apps they
  // can open, and the three account actions. Billing history, the pre-license
  // subscription and the raw audit log were dropped on purpose (2026-10-23).
  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }} className="acx__stack acx__stack--lg">
      <header className="acx__stack" style={{ alignItems: "flex-start" }}>
        <Link to="/admin" className="acx__btn acx__btn--ghost">{t("adminPanel.detail.back")}</Link>
        <UserSummary user={data.user} locale={locale} />
      </header>

      <AppAccessCard data={data} onChanged={reload} />

      <AccountActions data={data} />
    </div>
  );
}

function UserSummary({ user, locale }: { user: AdminUserDetail["user"]; locale: string }) {
  const { t } = useTranslation();
  return (
    <div className="acx__row" style={{ gap: "var(--acx-s-2) var(--acx-s-3)" }}>
      <ProviderBadge providerId={user.providerId} size={20} />
      <strong style={{ fontSize: "var(--acx-fs-lg)" }}>{user.email}</strong>
      {user.displayName && <span style={{ color: "var(--acx-text-muted)" }}>{user.displayName}</span>}
      <span className={`acx__pill ${user.emailVerified ? "acx__pill--active" : "acx__pill--unverified"}`}>
        {user.emailVerified ? t("adminPanel.detail.summary.verified") : t("adminPanel.detail.summary.unverified")}
      </span>
      {user.admin && <span className="acx__pill acx__pill--manual">{t("adminPanel.detail.summary.admin")}</span>}
      <span>
        {user.company || user.phone
          ? t("adminPanel.detail.summary.contact", { company: user.company ?? "—", phone: user.phone ?? "—" })
          : <span style={{ color: "var(--acx-text-muted)" }}>{t("adminPanel.detail.summary.noContact")}</span>}
      </span>
      <span style={{ color: "var(--acx-text-muted)", fontSize: "var(--acx-fs-sm)" }}>
        {t("adminPanel.detail.summary.dates", {
          created: fmtDateShort(user.createdAt, locale),
          lastSeen: relativeTime(user.lastSignInAt, locale)
        })}
      </span>
    </div>
  );
}

/** Default end date offered when granting: a year from today. */
function defaultExpiresAt(): string {
  const d = new Date(Date.now() + 365 * 86400_000);
  return d.toISOString().slice(0, 10);
}

/** yyyy-mm-dd of a timestamp, in local time, for an <input type="date">. */
function toDateInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * End of the chosen day in local time. A license "until March 12" should
 * still open on March 12, so it runs out at midnight, not at its first minute.
 */
function fromDateInput(value: string): number | null {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

/**
 * Which apps this user may open. One row per app in the catalog, so an app
 * added later shows up here with no change to this page. Payment happens
 * outside the platform: this only records access, and always until a date.
 */
function AppAccessCard({ data, onChanged }: { data: AdminUserDetail; onChanged: () => void }) {
  const { t } = useTranslation();
  const pending = data.licenseRequests.filter((r) => r.status === "pending");

  return (
    <section className="acx__card" id="apps">
      <div className="acx__stack acx__stack--lg">
        <h2 className="acx__card-title" style={{ fontSize: "var(--acx-fs-md)" }}>{t("adminPanel.detail.apps.section")}</h2>

        {pending.map((r) => (
          <PendingRequest key={r.appId} uid={data.user.uid} request={r} onChanged={onChanged} />
        ))}

        {!data.user.emailVerified && (
          <div className="acx__alert acx__alert--warning" role="status">{t("adminPanel.detail.apps.unverified")}</div>
        )}

        <div className="acx__stack">
          {APPS.map((app) => (
            <AppAccessRow
              key={app.appId}
              uid={data.user.uid}
              app={app}
              grant={data.appGrants.find((g) => g.appId === app.appId) ?? null}
              onChanged={onChanged}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PendingRequest({
  uid,
  request,
  onChanged
}: {
  uid: string;
  request: AdminUserDetail["licenseRequests"][number];
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);

  async function dismiss() {
    if (!confirm(t("adminPanel.detail.apps.confirmDismiss"))) return;
    setBusy(true);
    try {
      await adminDismissLicenseRequest({ uid, appId: request.appId });
      onChanged();
    } catch (e) {
      console.error("[admin-user-detail] dismiss failed", e);
      setBusy(false);
    }
  }

  return (
    <div className="acx__banner acx__banner--info" role="status">
      <strong>
        {t("adminPanel.detail.apps.requestTitle", {
          app: t(`home.apps.${request.appId}.name`),
          date: fmtDateShort(request.updatedAt, i18n.language)
        })}
      </strong>
      <span>
        {t("adminPanel.detail.apps.requestCompany", { company: request.company })}
        {request.message ? ` — “${request.message}”` : ""}
      </span>
      {request.notifiedAt === null && <span>{t("adminPanel.detail.apps.requestNotNotified")}</span>}
      <button type="button" onClick={dismiss} disabled={busy} className="acx__btn acx__btn--ghost" style={{ alignSelf: "flex-start" }}>
        {t("adminPanel.detail.apps.dismiss")}
      </button>
    </div>
  );
}

function AppAccessRow({
  uid,
  app,
  grant,
  onChanged
}: {
  uid: string;
  app: AppDefinition;
  grant: AppGrant | null;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const now = Date.now();
  const current = grant !== null && isGrantCurrent(grant, now);
  const [checked, setChecked] = useState(current);
  const [date, setDate] = useState(current && grant ? toDateInput(grant.expiresAt) : defaultExpiresAt());
  const [note, setNote] = useState(grant?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const appName = t(`home.apps.${app.appId}.name`);

  if (app.access === "free") {
    return (
      <div className="acx__app-access-row">
        <label className="acx__app-access-check">
          <input type="checkbox" checked disabled />
          <span className="acx__app-access-name">{appName}</span>
        </label>
        <span className="acx__pill acx__pill--active">{t("adminPanel.detail.apps.freeForAll")}</span>
      </div>
    );
  }

  const expiresAt = fromDateInput(date);
  const dirty =
    checked !== current ||
    (checked && current && grant !== null && expiresAt !== null && toDateInput(expiresAt) !== toDateInput(grant.expiresAt)) ||
    (checked && (note.trim() || null) !== (grant?.note ?? null));

  async function save() {
    setError(null);
    if (checked && (expiresAt === null || expiresAt <= Date.now())) {
      setError(t("adminPanel.detail.err.invalidDate"));
      return;
    }
    if (!checked && !confirm(t("adminPanel.detail.apps.confirmRevoke", { app: appName }))) return;
    setBusy(true);
    try {
      await adminSetAppAccess(
        checked
          ? { uid, appId: app.appId, enabled: true, expiresAt: expiresAt!, note: note.trim() || null }
          : { uid, appId: app.appId, enabled: false }
      );
      onChanged();
    } catch (e) {
      console.error("[admin-user-detail] set app access failed", e);
      setError(t("adminPanel.detail.err.action"));
      setBusy(false);
    }
  }

  return (
    <div className="acx__app-access-row acx__app-access-row--licensed">
      <div className="acx__row" style={{ justifyContent: "space-between", gap: "var(--acx-s-3)", flexWrap: "wrap" }}>
        <label className="acx__app-access-check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={busy} />
          <span className="acx__app-access-name">{appName}</span>
        </label>
        <GrantStatus grant={grant} current={current} locale={locale} />
      </div>

      {checked && (
        <div className="acx__row" style={{ gap: "var(--acx-s-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
          <label className="acx__stack" style={{ gap: "var(--acx-s-1)" }}>
            <span className="acx__info-label">{t("adminPanel.detail.apps.until")}</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="acx__input" disabled={busy} required />
          </label>
          <label className="acx__stack" style={{ gap: "var(--acx-s-1)", flex: 1, minWidth: 200 }}>
            <span className="acx__info-label">{t("adminPanel.detail.apps.note")}</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("adminPanel.detail.apps.notePlaceholder")}
              maxLength={500}
              className="acx__input"
              disabled={busy}
            />
          </label>
        </div>
      )}

      {dirty && (
        <div className="acx__row" style={{ gap: "var(--acx-s-3)" }}>
          <button type="button" onClick={save} disabled={busy} className={`acx__btn ${checked ? "acx__btn--primary" : "acx__btn--danger"}`}>
            {busy
              ? t("adminPanel.detail.working")
              : !checked
                ? t("adminPanel.detail.apps.revoke", { app: appName })
                : current
                  ? t("adminPanel.detail.apps.extend", { date: expiresAt ? fmtDateShort(expiresAt, locale) : "—" })
                  : t("adminPanel.detail.apps.grant", { app: appName, date: expiresAt ? fmtDateShort(expiresAt, locale) : "—" })}
          </button>
          <button
            type="button"
            onClick={() => {
              setChecked(current);
              setDate(current && grant ? toDateInput(grant.expiresAt) : defaultExpiresAt());
              setNote(grant?.note ?? "");
              setError(null);
            }}
            disabled={busy}
            className="acx__btn acx__btn--ghost"
          >
            {t("adminPanel.detail.apps.discard")}
          </button>
        </div>
      )}
      {error && <div className="acx__alert acx__alert--danger" role="alert">{error}</div>}
    </div>
  );
}

function GrantStatus({ grant, current, locale }: { grant: AppGrant | null; current: boolean; locale: string }) {
  const { t } = useTranslation();
  if (!grant) return <span className="acx__pill acx__pill--neutral">{t("adminPanel.detail.apps.never")}</span>;
  if (current) {
    return (
      <span className="acx__pill acx__pill--active">
        {t("adminPanel.detail.apps.activeUntil", { date: fmtDateShort(grant.expiresAt, locale) })}
      </span>
    );
  }
  if (grant.status === "revoked") {
    return (
      <span className="acx__pill acx__pill--neutral">
        {t("adminPanel.detail.apps.revokedOn", { date: fmtDateShort(grant.updatedAt, locale) })}
      </span>
    );
  }
  return (
    <span className="acx__pill acx__pill--cancelled">
      {t("adminPanel.detail.apps.expiredOn", { date: fmtDateShort(grant.expiresAt, locale) })}
    </span>
  );
}

type DeleteBlocker = "self" | "admin" | "active-paypal-sub" | null;

function deleteBlocker(data: AdminUserDetail, actorUid: string | null): DeleteBlocker {
  if (actorUid && actorUid === data.user.uid) return "self";
  if (data.user.admin) return "admin";
  const sub = data.activeSub;
  if (sub && (sub.status === "active" || sub.status === "past_due") && !sub.isManual) {
    return "active-paypal-sub";
  }
  return null;
}

/**
 * Password reset, resend verification and delete, in one card at the bottom:
 * the three things Frank does to an account besides opening apps.
 */
function AccountActions({ data }: { data: AdminUserDetail }) {
  const { t } = useTranslation();
  const { user } = useAuthCtx();
  const nav = useNavigate();
  const [busy, setBusy] = useState<"verify" | "reset" | "delete" | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const blocker = deleteBlocker(data, user?.uid ?? null);
  const blockedText = blocker ? t(`adminPanel.detail.delete.blocked.${blocker}`) : "";

  async function send(type: "VERIFY_EMAIL" | "PASSWORD_RESET", busyKey: "verify" | "reset") {
    setBusy(busyKey);
    setMsg(null);
    try {
      await adminSendOobCode({ uid: data.user.uid, type });
      const key = type === "VERIFY_EMAIL" ? "verifySent" : "resetSent";
      setMsg({ kind: "ok", text: t(`adminPanel.detail.quickActions.${key}`, { email: data.user.email }) });
    } catch (e) {
      console.error("[admin-user-detail] sendOobCode failed", e);
      setMsg({ kind: "err", text: t("adminPanel.detail.err.action") });
    } finally {
      setBusy(null);
    }
  }

  async function onDelete() {
    if (!confirm(t("adminPanel.detail.delete.confirm", { email: data.user.email }))) return;
    setBusy("delete");
    setMsg(null);
    try {
      await adminDeleteUser({ uid: data.user.uid });
      nav("/admin", { replace: true });
    } catch (e) {
      console.error("[admin-user-detail] delete failed", e);
      const text = e instanceof Error ? e.message : String(e);
      setMsg({ kind: "err", text: t("adminPanel.detail.delete.error", { msg: text }) });
      setBusy(null);
    }
  }

  return (
    <section className="acx__card">
      <div className="acx__stack acx__stack--lg">
        <h2 className="acx__card-title" style={{ fontSize: "var(--acx-fs-md)" }}>{t("adminPanel.detail.account.section")}</h2>
        <div className="acx__row" style={{ gap: "var(--acx-s-3)" }}>
          <button type="button" onClick={() => send("PASSWORD_RESET", "reset")} disabled={busy !== null} className="acx__btn acx__btn--ghost">
            {busy === "reset" ? t("adminPanel.detail.working") : t("adminPanel.detail.quickActions.sendReset")}
          </button>
          {!data.user.emailVerified && (
            <button type="button" onClick={() => send("VERIFY_EMAIL", "verify")} disabled={busy !== null} className="acx__btn acx__btn--ghost">
              {busy === "verify" ? t("adminPanel.detail.working") : t("adminPanel.detail.quickActions.resendVerify")}
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            disabled={busy !== null || blocker !== null}
            title={blockedText || undefined}
            className="acx__btn acx__btn--danger"
            style={{ marginLeft: "auto" }}
          >
            {busy === "delete" ? t("adminPanel.detail.working") : t("adminPanel.detail.delete.button")}
          </button>
        </div>
        {blocker && <p style={{ color: "var(--acx-text-muted)", fontSize: "var(--acx-fs-sm)", margin: 0 }}>{blockedText}</p>}
        {msg && (
          <div className={`acx__alert ${msg.kind === "ok" ? "acx__alert--info" : "acx__alert--danger"}`} role={msg.kind === "ok" ? "status" : "alert"}>
            {msg.text}
          </div>
        )}
      </div>
    </section>
  );
}
